import * as path from "node:path";

/**
 * Where a brain App's credentials live on disk.
 *
 * The layout separates the ACTIVE POINTER from the per-App files:
 *
 *   ~/.m8t/github-app-<org>-<appId>.pem    the private key      (per App, never overwritten)
 *   ~/.m8t/github-app-<org>-<appId>.json   that App's own creds (per App, never overwritten)
 *   ~/.m8t/github-app.json                 the active App       (a pointer; overwritten by design)
 *
 * The App id is load-bearing, not decoration: `m8t brain app-create --new`
 * deliberately creates a SECOND App on the SAME org, so a name keyed on the org
 * alone would still overwrite the first App's private key — the exact loss this
 * layout exists to prevent. Because GitHub App ids are globally unique, two
 * different Apps can never collide on a filename.
 *
 * Readers resolve the key through the `pemPath` field recorded in the creds
 * JSON, never by rebuilding it from this function. That is what lets an install
 * created before this layout keep working untouched: its JSON names the old
 * fixed path, the file is still there, and nothing needs migrating.
 */
export interface BrainAppFilePaths {
  /** The App's private key. */
  pemPath: string;
  /** The App's own copy of its credentials, kept beside the key. */
  sidecarPath: string;
}

/**
 * The active App — the file `m8t bootstrap launch` and the profile seeder read.
 * Fixed by contract: moving it would orphan every existing install, so this is a
 * single owner for the path rather than a value anything should recompute.
 */
export function brainAppPointerPath(home: string): string {
  return path.join(home, ".m8t", "github-app.json");
}

/**
 * Reduce an org login to a filename segment. GitHub logins are already
 * letters/digits/hyphens, so this is a no-op for real orgs; it exists so a
 * hostile or mistyped `--org` cannot steer a write out of `~/.m8t`. Sanitising
 * rather than refusing keeps a legitimate org from ever being rejected by a
 * guess about GitHub's naming rules, and the App id preserves uniqueness even
 * when two inputs reduce to the same segment.
 */
function orgFileSegment(org: string): string {
  return org.replace(/[^A-Za-z0-9-]+/g, "-");
}

export function brainAppFilePaths(home: string, org: string, appId: string): BrainAppFilePaths {
  const dir = path.join(home, ".m8t");
  const stem = `github-app-${orgFileSegment(org)}-${appId}`;
  return {
    pemPath: path.join(dir, `${stem}.pem`),
    sidecarPath: path.join(dir, `${stem}.json`),
  };
}

/** The fields a creds record must carry to be preservable as a sidecar. */
interface PreservableCreds { org: string; appId: string }

function isPreservable(v: unknown): v is PreservableCreds {
  if (typeof v !== "object" || v === null) return false;
  const c = v as { org?: unknown; appId?: unknown };
  return typeof c.org === "string" && c.org !== "" && typeof c.appId === "string" && c.appId !== "";
}

/**
 * Where the App currently named by the pointer should keep its own record.
 *
 * `app-create` overwrites the pointer by design, and an App created before the
 * per-App layout existed has no sidecar — its `appId` and `installationId` live
 * ONLY in the pointer. Overwriting it would destroy the record that makes its
 * (now safe) private key usable, and would silently break the documented
 * "copy a per-App JSON back over the pointer" procedure for exactly the App
 * that procedure exists to protect.
 *
 * Returns null when there is nothing to preserve: no pointer, unparseable, not
 * carrying an org+appId, or a sidecar already on disk.
 */
export function outgoingSidecarToPreserve(
  home: string,
  pointerText: string | null,
  sidecarExists: (p: string) => boolean,
): { path: string; org: string; appId: string } | null {
  if (pointerText === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(pointerText);
  } catch {
    return null;
  }
  if (!isPreservable(parsed)) return null;
  const { sidecarPath } = brainAppFilePaths(home, parsed.org, parsed.appId);
  if (sidecarExists(sidecarPath)) return null;
  return { path: sidecarPath, org: parsed.org, appId: parsed.appId };
}
